import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBedrockRegistration } from 'store';
import { Button } from 'components/Button';
import { RecaptchaForm } from 'components/AccountLoginRegistration/RecaptchaForm';
import { TextField } from 'components/FormFields/TextField';
import { ErrorMsg } from 'components/FormFields/ErrorMsg';
import { SocialLogin } from 'components/AccountLoginRegistration/SocialLogin';
import { CirclesBackground } from 'components/AccountLoginRegistration/Circles';
import Link from 'components/Link';
import { SCREENS } from 'lib/loginFormConstants';
import { useVertical } from 'lib/Hooks';
import cx from 'classnames';
import { loginSchema } from '../validationSchema';

import styles from './styles.module.scss';
import globalStyles from '../styles.module.scss';

/**
 * LogInScreen component for handling log in with email and password.
 * @param {object} props - The component props.
 * @param {string} props.pageView - The page type.
 * @param {string} props.currentScreen - current screen.
 * @returns {React.ReactElement} The rendered component.
 */
const LoginPasswordScreen = ({ pageView, currentScreen }) => {
  const setScreen = useBedrockRegistration((state) => state.setScreen);
  const userSignin = useBedrockRegistration((state) => state.userSignin);
  const track = useBedrockRegistration((state) => state.track);
  const error = useBedrockRegistration((state) => state.error);
  const { vertical } = useVertical();

  /**
   * Handles form submission for logging in with email and password.
   * @param {object} data - The form data containing email and password.
   */
  const onSubmit = (data) => {
    const trackingPayload = {
      action: 'SignIn:Submit',
      method: 'SubmitButton:Click',
    };
    userSignin({
      email: data.email,
      password: data.password,
      brand: vertical,
      trackingPayload,
    })
      .then((result) => {
        // update the screen value based on the response
        if (result) {
          setScreen(result);
        }
      });
  };

  /**
   * Handles one time code click
   * passes payload to requestOneTimeCode for tracking
   * sets screen on success
   */
  const handleOTCClick = () => {
    track({
      action: 'SignIn:Click',
      method: 'OneTimeCodeButton:Click',
    });
    setScreen(SCREENS.LOGIN_OTC_EMAIL);
  };

  const methods = useForm({
    resolver: yupResolver(loginSchema),
  });

  return (
    <>
      <h2 data-testid="account-login">Log In</h2>
      <ErrorMsg message={error} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <RecaptchaForm
          submitCallback={onSubmit}
        >
          <p>
            Don&apos;t have a login?&nbsp;
            <button
              type="button"
              className={cx(globalStyles.buttonLink, styles.buttonLink)}
              onClick={() => {
                track({
                  action: 'SignIn:Submit',
                  method: 'SignUp:Click',
                });
                setScreen(SCREENS.REGISTER);
              }}
              data-testid="create-account-button"
            >
              Create Account
            </button>
          </p>
          <TextField
            name="email"
            type="email"
            dataTestId="email-input-field"
            placeholder="Email"
            className={styles.input}
            isRequired
          />
          <TextField
            name="password"
            type="password"
            dataTestId="password-input-field"
            placeholder="Password"
            isRequired
          />
          <Button
            tiltClasses={globalStyles.button}
            isFullWidth
            displayType="submit"
            title="Log In"
          />
        </RecaptchaForm>
      </FormProvider>
      <div className={styles.links}>
        <div>
          <button
            type="button"
            onClick={handleOTCClick}
            className={globalStyles.buttonLink}
          >
            Log in with one time code
          </button>
        </div>
        <div><Link to="https://profile.id.nbcuni.com/forgot-password" target="_blank" className={globalStyles.buttonLink}>Forgot Password</Link></div>
      </div>
      <div className={styles.separator}>
        <span>or</span>
      </div>
      <div className={styles.bottom}>
        <SocialLogin
          pageView={pageView}
          currentScreen={currentScreen}
        />
        <button
          type="button"
          className={globalStyles.buttonLink}
          onClick={() => {
            track({
              action: 'SignUp:Submit',
              method: 'PoweredByNBCUButton:Click',
            });
            setScreen(SCREENS.NBCU_PROFILE);
          }}
          data-testid="nbcu-profile-button"
        >
        Powered by NBCUniversal Profile
        </button>
      </div>
      <CirclesBackground />
    </>
  );
};

export { LoginPasswordScreen };
